import { useEffect, useState, Component } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Stakes } from "@/components/Stakes";
import { Synergy } from "@/components/Synergy";
import { Products } from "@/components/Products";
import { SpecTable } from "@/components/SpecTable";
import { Tech } from "@/components/Tech";
import { Expert } from "@/components/Expert";
import { Plan } from "@/components/Plan";
import { Faq } from "@/components/Faq";
import { CaseStudy } from "@/components/CaseStudy";
import { Admin } from "@/components/Admin";
import LegalPage from "@/components/LegalPage";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";

class ErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error("App boundary:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen grid place-items-center bg-ink text-txt-2 font-mono2 text-sm px-6 text-center">
          Something went wrong — please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [route, setRoute] = useState(window.location.hash);
  useEffect(() => {
    const onHash = () => setRoute(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (window.location.hash.startsWith("#/admin")) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  if (route.startsWith("#/admin")) {
    return (
      <ErrorBoundary>
        <Admin />
        <Toaster position="bottom-right" theme="dark" />
      </ErrorBoundary>
    );
  }

  if (route.startsWith("#/privacy") || route.startsWith("#/imprint")) {
    const kind = route.startsWith("#/imprint") ? "imprint" : "privacy";
    return (
      <ErrorBoundary>
        <LanguageProvider>
          <LegalPage kind={kind} />
          <Toaster position="bottom-right" theme="dark" />
        </LanguageProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <div className="bg-ink text-txt font-body min-h-screen">
          <Navbar />
          <main>
            <Hero />
            <Marquee />
            <Stakes />
            <Synergy />
            <Products />
            <SpecTable />
            <Tech />
            <Expert />
            <Plan />
            <CaseStudy />
            <Faq />
            <Contact />
          </main>
          <Footer />
          <Toaster position="bottom-right" theme="dark" />
        </div>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
